import { AudioContextManager } from './sound/AudioContext';
import { SoundEffects } from './sound/SoundEffects';
import { BGMManager, type BGMTrackData } from './sound/BGMManager';
import { CONFIG } from '../config';

type BorderDirection = 'left' | 'right' | 'top' | 'bottom';

interface SavedOptions {
    sfx?: boolean;
    bgm?: boolean;
    bgmTrack?: string;
}

/**
 * 사운드 관리 통합 클래스
 * AudioContext, 효과음, BGM을 통합 관리
 */
export class SoundManager {
    private audioContext: AudioContextManager;
    private soundEffects: SoundEffects;
    private bgmManager: BGMManager;
    private sfxEnabled: boolean;
    private bgmEnabled: boolean;

    constructor() {
        this.audioContext = new AudioContextManager();
        this.audioContext.init();

        this.soundEffects = new SoundEffects(this.audioContext);
        this.bgmManager = new BGMManager();

        // 옵션에서 토글 상태 로드
        this.sfxEnabled = true;
        this.bgmEnabled = true;
        this.loadOptions();
    }

    loadOptions(): void {
        try {
            const saved = localStorage.getItem(CONFIG.OPTIONS.STORAGE_KEY);
            if (saved) {
                const options: SavedOptions = JSON.parse(saved);
                this.sfxEnabled = options.sfx !== undefined ? options.sfx : true;
                this.bgmEnabled = options.bgm !== undefined ? options.bgm : true;
                // 선택된 BGM 트랙 로드
                if (options.bgmTrack) {
                    this.bgmManager.setSelectedTrack(options.bgmTrack);
                }
            }
        } catch (e) {
            console.warn('Failed to load sound options:', e);
        }
    }

    setSfxEnabled(enabled: boolean): void {
        this.sfxEnabled = enabled;
    }

    setBgmEnabled(enabled: boolean): void {
        this.bgmEnabled = enabled;
    }

    // Audio Context
    unlockAudio(): void {
        this.audioContext.unlock();
    }

    // Sound Effects
    playBulletBoundarySound(border: BorderDirection = 'left'): void {
        if (!this.sfxEnabled) return;
        this.soundEffects.playBulletBoundarySound(border);
    }

    playCollisionMelody(): void {
        if (!this.sfxEnabled) return;
        this.soundEffects.playCollisionMelody();
    }

    playHeartSound(): void {
        if (!this.sfxEnabled) return;
        this.soundEffects.playHeartSound();
    }

    playStarSound(): void {
        if (!this.sfxEnabled) return;
        this.soundEffects.playStarSound();
    }

    playItemSpawnSound(): void {
        if (!this.sfxEnabled) return;
        this.soundEffects.playItemSpawnSound();
    }

    // New sound effects
    playEvolveSound(): void {
        if (!this.sfxEnabled) return;
        this.soundEffects.playEvolveSound();
    }

    playDevolveSound(): void {
        if (!this.sfxEnabled) return;
        this.soundEffects.playDevolveSound();
    }

    playNearMissSound(): void {
        if (!this.sfxEnabled) return;
        this.soundEffects.playNearMissSound();
    }

    playComboSound(combo: number): void {
        if (!this.sfxEnabled) return;
        this.soundEffects.playComboSound(combo);
    }

    playBossWarning(): void {
        if (!this.sfxEnabled) return;
        this.soundEffects.playBossWarning();
    }

    playPowerUpSound(): void {
        if (!this.sfxEnabled) return;
        this.soundEffects.playPowerUpSound();
    }

    playBombSound(): void {
        if (!this.sfxEnabled) return;
        this.soundEffects.playBombSound();
    }

    playBossDefeatSound(): void {
        if (!this.sfxEnabled) return;
        this.soundEffects.playBossDefeatSound();
    }

    // BGM
    async loadBGM(): Promise<void> {
        await this.bgmManager.loadSelectedTrack();
    }

    async playBGM(): Promise<void> {
        // 옵션에서 최신 상태 다시 로드
        this.loadOptions();
        if (!this.bgmEnabled) return;
        await this.bgmManager.play();
    }

    /**
     * BGM 트랙 설정
     */
    setSelectedBGMTrack(trackId: string): void {
        this.bgmManager.setSelectedTrack(trackId);
    }

    /**
     * 현재 선택된 BGM 트랙 ID 반환
     */
    getSelectedBGMTrack(): string {
        return this.bgmManager.getSelectedTrack();
    }

    /**
     * 모든 BGM 트랙 목록 반환
     */
    getAllBGMTracks(): BGMTrackData[] {
        return this.bgmManager.getAllTracks();
    }

    stopBGM(): void {
        this.bgmManager.stop();
    }

    setBGMVolume(volume: number): void {
        this.bgmManager.setVolume(volume);
    }

    getAudioIntensity(): number {
        return this.bgmManager.getIntensity();
    }

    getCurrentTrackTitle(): string | null {
        return this.bgmManager.getCurrentTrackTitle();
    }

    async switchToBossBGM(): Promise<void> {
        if (!this.bgmEnabled) return;
        await this.bgmManager.switchToBossBGM();
    }

    async switchToNormalBGM(): Promise<void> {
        if (!this.bgmEnabled) return;
        await this.bgmManager.switchToNormalBGM();
    }

    // Pause/Resume all audio (for background/foreground switching)
    async pauseAll(): Promise<void> {
        this.bgmManager.pause();
        await this.audioContext.suspend();
    }

    async resumeAll(): Promise<void> {
        this.bgmManager.resume();
        await this.audioContext.resume();
    }

    destroy(): void {
        this.bgmManager.stop();
        this.audioContext.close();
    }
}
